<script setup lang="ts">
import { computed, ref } from "vue";

import type { FollowingLineProps, FollowingSegment } from "@/components/following/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import Dropdown from "@/dsComponents/Dropdown";

import FollowingInterviewPopover from "@/components/following/FollowingInterviewPopover.vue";

const props = withDefaults(defineProps<FollowingLineProps>(), {
    segments: () => [],
    icon: "",
    detail: "",
    showThreadLine: true,
    rightAction: undefined,
    onLinkNavigate: undefined,
});

const handleLinkSegmentClick = (segment: FollowingSegment, event: MouseEvent) => {
    if (segment.type !== "link" || !props.onLinkNavigate) return;
    event.preventDefault();
    event.stopPropagation();
    props.onLinkNavigate(segment.to, event);
};

defineSlots<{
    icon?: () => unknown;
    main?: () => unknown;
    detail?: () => unknown;
    right?: () => unknown;
    bottom?: () => unknown;
    "dropdown-content"?: (props: { segment: FollowingSegment; index: number; adjustPosition: () => Promise<void> }) => unknown;
}>();

const isOpenBySegmentKey = ref<Record<string, boolean>>({});

const getDropdownKey = (segment: FollowingSegment, index: number): string => {
    if (segment.type === "dropdown" && segment.id) return segment.id;
    return `${segment.type}-${index}`;
};

const handleDropdownToggle = (key: string, isOpen: boolean): void => {
    isOpenBySegmentKey.value[key] = isOpen;
};

const hasMultilineSegment = computed(() => props.segments.some((segment) => segment.type === "text" && segment.multiline));

const getSegmentIconColor = (segment: FollowingSegment): string | undefined => {
    if (segment.type === "text") return undefined;
    if (segment.iconColor) return segment.iconColor;
    if (segment.leftIcon && typeof segment.leftIcon === "object" && "color" in segment.leftIcon) {
        return segment.leftIcon.color;
    }

    return undefined;
};
</script>

<template>
    <div class="following-line" role="listitem">
        <div class="following-line__icon">
            <slot name="icon">
                <i v-if="icon" :class="`ti ti-${icon} typo-sm-regular`" />
            </slot>
        </div>

        <div class="following-line__body">
            <div class="following-line__content-wrapper" :class="{ 'following-line__content-wrapper--no-gap': hasMultilineSegment }">
                <div class="following-line__content" :class="{ 'following-line__content--spaced': hasMultilineSegment }">
                    <slot name="main">
                        <div class="following-line__main">
                            <template v-for="(segment, index) in segments" :key="`${segment.type}-${index}`">
                                <span
                                    v-if="segment.type === 'text'"
                                    class="following-line__segment-text typo-xxs-medium"
                                    :class="{ 'following-line__segment-text--multiline': segment.multiline }"
                                >
                                    {{ segment.content }}
                                </span>

                                <ButtonAlt
                                    v-else-if="segment.type === 'link'"
                                    class="following-line__segment-button"
                                    size="xs"
                                    type="neutral"
                                    variant="ghost"
                                    :label="segment.label"
                                    :left-icon="segment.leftIcon"
                                    :left-icon-color="getSegmentIconColor(segment)"
                                    :to="onLinkNavigate ? undefined : segment.to"
                                    @click="handleLinkSegmentClick(segment, $event)"
                                />

                                <ButtonAlt
                                    v-else-if="segment.type === 'action'"
                                    class="following-line__segment-button"
                                    size="xs"
                                    type="neutral"
                                    variant="ghost"
                                    :label="segment.label"
                                    :left-icon="segment.leftIcon"
                                    :left-icon-color="getSegmentIconColor(segment)"
                                    @click="segment.onClick($event)"
                                />

                                <ButtonAlt
                                    v-else-if="segment.type === 'readonly'"
                                    class="following-line__segment-button"
                                    size="xs"
                                    type="neutral"
                                    variant="ghost"
                                    readonly
                                    :label="segment.label"
                                    :left-icon="segment.leftIcon"
                                    :left-icon-color="getSegmentIconColor(segment)"
                                />

                                <ButtonAlt
                                    v-else-if="segment.type === 'status'"
                                    class="following-line__segment-button"
                                    size="xs"
                                    type="neutral"
                                    variant="ghost"
                                    readonly
                                    :label="segment.label"
                                    :left-icon="segment.leftIcon"
                                    :left-icon-color="getSegmentIconColor(segment)"
                                />

                                <Dropdown
                                    v-else-if="segment.type === 'dropdown'"
                                    class="following-line__dropdown"
                                    fit-content
                                    :mode="segment.mode ?? 'click'"
                                    :width="segment.width ?? '300px'"
                                    @toggle="(isOpen) => handleDropdownToggle(getDropdownKey(segment, index), isOpen)"
                                >
                                    <template #trigger-zone>
                                        <ButtonAlt
                                            class="following-line__segment-button"
                                            :class="{ 'following-line__segment-button--open': isOpenBySegmentKey[getDropdownKey(segment, index)] }"
                                            size="xs"
                                            type="neutral"
                                            variant="ghost"
                                            :label="segment.label"
                                            :left-icon="segment.leftIcon"
                                            :left-icon-color="getSegmentIconColor(segment)"
                                        />
                                    </template>
                                    <template #dropdown-content="{ adjustPosition }">
                                        <slot name="dropdown-content" :segment="segment" :index="index" :adjust-position="adjustPosition">
                                            <FollowingInterviewPopover v-if="segment.data?.type === 'interview'" :data="segment.data.payload" />
                                            <p v-else-if="segment.data?.type === 'text'" class="following-line__dropdown-text">
                                                {{ segment.data.payload }}
                                            </p>
                                        </slot>
                                    </template>
                                </Dropdown>
                            </template>
                        </div>
                    </slot>

                    <div v-if="detail || $slots.detail" class="following-line__detail">
                        <slot name="detail">{{ detail }}</slot>
                    </div>
                </div>

                <div v-if="$slots.right || rightAction" class="following-line__right">
                    <slot name="right">
                        <ButtonAlt
                            v-if="rightAction"
                            size="sm"
                            type="neutral"
                            variant="stroke"
                            :label="rightAction.label"
                            @click="rightAction.onClick($event)"
                        />
                    </slot>
                </div>
            </div>

            <div v-if="$slots.bottom" class="following-line__bottom">
                <slot name="bottom" />
            </div>
        </div>

        <div v-if="showThreadLine" class="following-line__thread-line" />
    </div>
</template>

<style scoped lang="scss">
.following-line {
    display: flex;
    gap: var(--spacing-lg, 12px);
    padding-left: var(--spacing-lg, 12px);
    position: relative;
    border-radius: var(--radius-md, 8px);

    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: var(--spacing-2xl, 20px);
        flex-shrink: 0;

        :deep(i) {
            color: var(--icon-sub-500, #636778);
        }
    }

    &__body {
        display: flex;
        flex: 1;
        min-width: 0;
        flex-direction: column;
        gap: var(--spacing-xs, 4px);
    }

    &__content-wrapper {
        display: flex;
        gap: var(--spacing-4xl, 32px);
        width: 100%;
        min-width: 0;
        align-items: flex-start;

        &--no-gap {
            gap: 0;
        }
    }

    &__content {
        display: flex;
        flex: 1;
        min-width: 0;
        flex-direction: column;

        &--spaced {
            gap: var(--spacing-md, 8px);
        }
    }

    &__main {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--spacing-xxs, 2px);
        min-height: var(--spacing-2xl, 20px);
        width: 100%;
    }

    &__segment-text {
        color: var(--text-sub-600, #494e61);
        white-space: nowrap;

        &--multiline {
            white-space: pre-wrap;
            flex: 1;
            min-width: 0;
        }
    }

    &__dropdown {
        display: flex;
    }

    &__segment-button {
        :deep(.btn-alt-label) {
            padding: 0;
            font-size: 12px;
        }

        :deep(i) {
            width: 12px;
            height: 12px;
            font-size: 12px;
        }
    }

    &__detail {
        font-size: 11px;
        font-style: italic;
        color: var(--text-sub-600, #494e61);
    }

    &__dropdown-text {
        padding: var(--spacing-sm, 8px) var(--spacing-md, 12px);
        @include font-size-12;
        color: var(--text-sub-600, #494e61);
        max-width: 240px;
    }

    &__right {
        display: flex;
        flex-shrink: 0;
    }

    &__bottom {
        width: 100%;
    }

    &__thread-line {
        position: absolute;
        top: var(--spacing-2xl, 20px);
        bottom: calc(-1 * var(--spacing-xl, 16px));
        left: 18px;
        width: 0.5px;
        background-color: var(--stroke-soft-200, #dbdfe6);
    }
}
</style>
